import React, { useState, useEffect, useCallback } from "react";
import { getDocs, collection } from "firebase/firestore";
import { firestoreProd } from "../../firebase";
import { FaSyncAlt, FaRadiation } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDarkMode } from "../../components/Theme/useDarkMode";

const TEN_MINUTES_MS = 10 * 60 * 1000;
const ONE_HOUR_MS = 60 * 60 * 1000;

function getAgeMs(triggeredAt) {
  return Date.now() - triggeredAt;
}

function getRowBgColor(triggeredAt, isDark) {
  const age = getAgeMs(triggeredAt);
  if (age >= ONE_HOUR_MS) return isDark ? "rgba(239,68,68,0.15)" : "rgba(239,68,68,0.08)";
  if (age >= TEN_MINUTES_MS) return isDark ? "rgba(245,158,11,0.15)" : "rgba(245,158,11,0.08)";
  return null;
}

function formatAge(triggeredAt) {
  const totalSeconds = Math.floor(getAgeMs(triggeredAt) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ago`;
  if (minutes > 0) return `${minutes}m ${seconds}s ago`;
  return `${seconds}s ago`;
}

function formatScope(scope) {
  if (!scope) return "—";
  if (scope.type === "full") return "Full checklist";
  if (scope.type === "category") return `Category: ${scope.categoryId}`;
  if (scope.type === "tasks") return `Tasks: ${(scope.taskIds || []).join(", ")}`;
  return scope.type;
}

function formatTriggeredAt(triggeredAt) {
  return new Date(triggeredAt).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

const PCWatchdogPage = ({ onReady }) => {
  const [theme] = useDarkMode();
  const isDark = theme === "dark";

  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const cardPanelStyle = {
    background: isDark ? "#161b2e" : "#fff",
    border: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}`,
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: isDark
      ? "0 4px 24px rgba(0,0,0,0.3)"
      : "0 2px 12px rgba(0,0,0,0.06)",
  };

  const thStyle = {
    textAlign: "left",
    padding: "10px 16px",
    borderBottom: `2px solid ${isDark ? "#2d3148" : "#e2e8f0"}`,
    fontWeight: 600,
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: isDark ? "#718096" : "#6b7280",
    whiteSpace: "nowrap",
  };

  const fetchRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const snapshot = await getDocs(
        collection(firestoreProd, "processing-checklist-requests")
      );
      const docs = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.triggeredAt || 0) - (a.triggeredAt || 0));
      setRecords(docs);
      setLastRefreshed(Date.now());
    } catch (err) {
      console.error("Error fetching processing-checklist-requests:", err);
      toast.error("Failed to load records");
    } finally {
      setIsLoading(false);
      onReady?.();
    }
  }, [onReady]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Re-render every 10s to keep age labels and row colors current
  useEffect(() => {
    const interval = setInterval(() => setLastRefreshed((t) => t), 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="container-fluid p-4 PCWatchdogPageContainer"
      style={{
        color: isDark ? "#e2e8f0" : "#1a202c",
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        minHeight: "100vh",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme={isDark ? "dark" : "light"}
      />

      <div style={{ marginBottom: "28px" }}>
        <h1
          style={{
            margin: 0,
            fontSize: "24px",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <FaRadiation style={{ color: "#7F7FD5" }} />
          PC Watchdog
        </h1>
        <p style={{ margin: "6px 0 0", color: "#888", fontSize: "14px" }}>
          Active processing checklist requests. Records disappear once the run completes.
        </p>
      </div>

      <div style={cardPanelStyle}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "3px 12px",
                borderRadius: "20px",
                fontSize: "13px",
                fontWeight: 600,
                background: isDark ? "#2d3148" : "#ede9fe",
                color: "#7F7FD5",
              }}
            >
              {records.length} active
            </span>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                fontSize: "13px",
                color: isDark ? "#718096" : "#6b7280",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    background: isDark ? "#e2e8f0" : "#374151",
                    display: "inline-block",
                  }}
                />
                Under 10 min
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    background: "#f59e0b",
                    display: "inline-block",
                  }}
                />
                10 min – 1 h
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    background: "#ef4444",
                    display: "inline-block",
                  }}
                />
                Over 1 h
              </span>
            </div>
          </div>

          <div
            style={{ display: "flex", alignItems: "center", gap: "12px" }}
          >
            {lastRefreshed && (
              <span style={{ fontSize: "13px", color: isDark ? "#718096" : "#9ca3af" }}>
                Refreshed {new Date(lastRefreshed).toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={fetchRecords}
              disabled={isLoading}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 16px",
                fontSize: "13px",
                fontWeight: 600,
                border: "none",
                borderRadius: "8px",
                cursor: isLoading ? "not-allowed" : "pointer",
                background: "#7F7FD5",
                color: "#fff",
                opacity: isLoading ? 0.7 : 1,
                transition: "opacity 0.15s",
              }}
            >
              <FaSyncAlt style={{ fontSize: "12px" }} />
              {isLoading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>

        <div
          style={{
            overflowX: "auto",
            borderRadius: "8px",
            border: `1px solid ${isDark ? "#2d3148" : "#e2e8f0"}`,
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "13px",
            }}
          >
            <thead>
              <tr style={{ background: isDark ? "#1a1f35" : "#f8fafc" }}>
                <th style={thStyle}>Organization ID</th>
                <th style={thStyle}>Loan ID</th>
                <th style={thStyle}>Scope</th>
                <th style={thStyle}>Triggered At</th>
                <th style={thStyle}>Age</th>
                <th style={thStyle}>Watchdog Retries</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && records.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      textAlign: "center",
                      padding: "48px",
                      color: isDark ? "#718096" : "#9ca3af",
                    }}
                  >
                    Loading…
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      textAlign: "center",
                      padding: "48px",
                      color: isDark ? "#718096" : "#9ca3af",
                      fontSize: "15px",
                    }}
                  >
                    All clear — no active processing checklist requests.
                  </td>
                </tr>
              ) : (
                records.map((record, index) => {
                  const alertBg = getRowBgColor(record.triggeredAt, isDark);
                  const isEven = index % 2 === 0;
                  const defaultRowBg = isDark
                    ? isEven ? "transparent" : "#111527"
                    : isEven ? "transparent" : "#f8fafc";

                  const cellStyle = {
                    padding: "12px 16px",
                    borderBottom: `1px solid ${isDark ? "#2d3148" : "#f1f5f9"}`,
                    color: isDark ? "#e2e8f0" : "#1a202c",
                    verticalAlign: "top",
                    background: alertBg || defaultRowBg,
                  };

                  return (
                    <tr key={record.id}>
                      <td style={cellStyle}>
                        {record.organizationId || "—"}
                      </td>
                      <td style={cellStyle}>
                        {record.loanId ? (
                          <a
                            href={`https://app.addy.so/loans/${record.loanId}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              color: "#7F7FD5",
                              textDecoration: "none",
                              fontWeight: 600,
                            }}
                          >
                            {record.loanId}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td style={cellStyle}>
                        {formatScope(record.scope)}
                      </td>
                      <td style={cellStyle}>
                        {record.triggeredAt
                          ? formatTriggeredAt(record.triggeredAt)
                          : "—"}
                      </td>
                      <td style={cellStyle}>
                        {record.triggeredAt ? formatAge(record.triggeredAt) : "—"}
                      </td>
                      <td style={cellStyle}>
                        {record.watchdogExecutionCount ?? 0}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PCWatchdogPage;
